import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
  Checkbox,
  useMediaQuery,
  useTheme,
  Backdrop,
  LinearProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateMoveService } from "../../redux/action/move-service";
import { unwrapResult } from "@reduxjs/toolkit";
import { getRate } from "../../redux/action/rate";
import Header from "../header/Header";
import { getCarrierByBusinessName } from "../../redux/action/carrier";

interface BulkyItem {
  name: string;
  fee: number;
}

const MovingItems = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [bulkyItems, setBulkyItems] = useState<BulkyItem[]>([]);
  const [carrier, setCarrier] = useState<any>(null);
  const { canonicalId } = useParams();
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const carrierId = localStorage.getItem("CarrierId");
  const navigate = useNavigate();
  const { businessName, carrierCanonicalId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const lastStep = 4;
  const handleItemChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    setSelectedItems((prevSelectedItems) => {
      if (checked) {
        return [...prevSelectedItems, value];
      } else {
        return prevSelectedItems.filter((item) => item !== value);
      }
    });
  };
  useEffect(() => {
    const updateLastStep = async () => {
      const moveServiceData = {
        canonical_id: canonicalId,
        last_step: lastStep,
      };
      await dispatch<any>(updateMoveService(moveServiceData))
        .then(unwrapResult)
        .then((res: any) => {});
    };

    if (canonicalId) {
      updateLastStep();
    }
  }, [canonicalId, dispatch, lastStep]);
  const handleContinue = async () => {
    try {
      setIsLoading(true);
      setLoadingMessage("Saving your bulky items selection...");

      const itemsWithPrices = bulkyItems
        .filter((item) => selectedItems.includes(item.name))
        .map((item) => ({
          name: item.name,
          fee: item.fee,
        }));

      const bulkyItemPrice = itemsWithPrices.reduce(
        (sum, item) => sum + item.fee,
        0
      );

      const moveServiceData = {
        canonical_id: canonicalId,
        items: JSON.stringify(itemsWithPrices),
      };

      console.log(moveServiceData.items, "ITEMS");

      setTimeout(async () => {
        try {
          const res = await dispatch<any>(updateMoveService(moveServiceData));
          unwrapResult(res);
          const offersInsurance = carrier?.insurance != null;
          setLoadingMessage(
            offersInsurance
              ? "Redirecting to insurance page..."
              : "Redirecting to price calculation..."
          );
          if (offersInsurance) {
            navigate(
              `/${businessName}/${carrierCanonicalId}/insurance/${canonicalId}`
            );
          } else {
            navigate(
              `/${businessName}/${carrierCanonicalId}/move-price/${canonicalId}`
            );
          }
        } catch (err: any) {
          setError(err?.message || "Failed to update move service");
        } finally {
          setIsLoading(false);
        }
      }, 2000);
    } catch (err) {
      setIsLoading(false);
      setError("An unexpected error occurred");
    }
  };

  useEffect(() => {
    if (businessName && bulkyItems.length === 0) {
      setIsLoading(true);
      setLoadingMessage("Loading bulky items...");
      dispatch<any>(getCarrierByBusinessName(businessName))
        .then(unwrapResult)
        .then((res: any) => {
          setCarrier(res);
          if (res.rate.bulky_fee_list) {
            const items = Object.entries(res.rate.bulky_fee_list).map(
              ([name, fee]) => ({
                name,
                fee: Number(fee),
              })
            );
            setBulkyItems(items);
          }
        })
        .catch((err: any) => {
          console.log(err, "error occur");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [businessName]);
  const handleBack = () => {
    navigate(-1);
  };
  return (
    <div>
      <Header />
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
          onClick={() => {
            setIsLoading(true);
          }}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Container
        sx={{
          width: "100%",
          height: isMobile ? "93vh" : "80vh",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          padding: isMobile ? "20px" : "20px",
          background: "#f2f0f0",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflowY: "auto",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            fontWeight={700}
            fontSize={isMobile ? "16px" : "18px"}
            textAlign="center"
            mb={2}
          >
            Select Bulky Items
          </Typography>
          <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

          <Box
            sx={{
              overflowY: "auto",
              maxHeight: "540px",
            }}
          >
            <Typography mb={5}>
              Following items are considered as bulky by your carrier. If you
              are moving them along, please select them.
            </Typography>
            {bulkyItems.map((item) => (
              <Box
                key={item.name}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  textAlign: "left",
                  padding: "8px 0",
                  borderBottom: "1px solid #D3D3D3",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography fontSize={16}>
                    {item.name.charAt(0).toUpperCase() + item.name.slice(1)}
                  </Typography>
                  <Typography fontSize={12} color="text.secondary">
                    Fee: ${item.fee}
                  </Typography>
                </Box>
                <Checkbox
                  checked={selectedItems.includes(item.name)}
                  onChange={handleItemChange}
                  value={item.name}
                  sx={{ color: theme.palette.primary.main }}
                />
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: "20px",
              marginTop: isMobile ? "126px" : isMd ? "5px" : "68px",
            }}
          >
            <Button
              variant="outlined"
              sx={{
                borderRadius: "20px",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                padding: isMobile ? "8px 20px" : "8px 45px",
                textTransform: "none",
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: "600",
              }}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              sx={{
                borderRadius: "20px",
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                padding: isMobile ? "8px 20px" : "8px 45px",
                textTransform: "none",
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: "600",
                "&:hover": { backgroundColor: "#357ABD" },
              }}
              onClick={handleContinue}
              disabled={isLoading}
            >
              Continue
            </Button>
          </Box>
        </Box>
      </Container>
    </div>
  );
};

export default MovingItems;
